// The AI memo of a Case or a person: the handover (one text, rewritten whole,
// with a version) and the log (entries only ever added; people may delete one).
import {
  type InfiniteData,
  infiniteQueryOptions,
  type QueryClient,
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useApi } from "../app/context";
import type { Api } from "../data/api";
import { optimisticMutation } from "../data/optimistic";

export interface Memo {
  body: string;
  version: number;
  logCount: number;
}

export interface MemoLogEntry {
  id: string;
  body: string;
  createdAt: string;
  topicId: string | null;
}

interface MemoLogPage {
  entries: MemoLogEntry[];
  nextCursor: string | null;
}

export const memoPrefix = (kind: string, id?: string) =>
  id === undefined ? ["me", "memos", kind] : ["me", "memos", kind, id];

const memoPath = (kind: string, id: string) =>
  `/api/me/memos/${encodeURIComponent(kind)}/${encodeURIComponent(id)}`;

export const memoQuery = (api: Api, kind: string, id: string) =>
  queryOptions({
    queryKey: [...memoPrefix(kind, id), "handover"],
    queryFn: () => api<Memo>(memoPath(kind, id)),
  });

const logKey = (kind: string, id: string) => [...memoPrefix(kind, id), "log"];

export const memoLogQuery = (api: Api, kind: string, id: string) =>
  infiniteQueryOptions({
    queryKey: logKey(kind, id),
    queryFn: ({ pageParam }) =>
      api<MemoLogPage>(
        `${memoPath(kind, id)}/log?limit=50${pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ""}`,
      ),
    initialPageParam: "",
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

export const invalidateMemo = (qc: QueryClient, kind: string, id?: string) =>
  qc.invalidateQueries({ queryKey: memoPrefix(kind, id) });

export function useSaveMemo(kind: string, id: string) {
  const api = useApi();
  const qc = useQueryClient();
  const key = [...memoPrefix(kind, id), "handover"];
  return useMutation({
    mutationFn: (input: { body: string; version: number }) =>
      api<{ body: string; version: number }>(memoPath(kind, id), {
        method: "PUT",
        body: input,
      }),
    ...optimisticMutation<Memo, { body: string; version: number }>(
      qc,
      key,
      (old, input) => old && { ...old, body: input.body },
    ),
  });
}

export function useDeleteMemoEntry(kind: string, id: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { entryId: string }) =>
      api(`${memoPath(kind, id)}/log/${encodeURIComponent(input.entryId)}`, {
        method: "DELETE",
      }),
    ...optimisticMutation<InfiniteData<MemoLogPage>, { entryId: string }>(
      qc,
      logKey(kind, id),
      (old, input) =>
        old && {
          ...old,
          pages: old.pages.map((p) => ({
            ...p,
            entries: p.entries.filter((e) => e.id !== input.entryId),
          })),
        },
    ),
  });
}
